/**
 * Product Image Click-to-Zoom Functionality
 * Adds zoom-in effect when product images are clicked on PDP
 */

class ProductImageZoom {
  constructor() {
    // Store mobile state
    this.mobile = this.isMobile();
    
    // Immediately check if mobile - if not, don't do anything
    if (!this.mobile) {
      return; // Exit early on desktop
    }
    this.init();
  }

  isMobile() {
    // Check if device is mobile based on viewport width
    // Using 750px as breakpoint (matches CSS media query)
    return window.innerWidth <= 750 || window.matchMedia('(max-width: 750px)').matches;
  }

  init() {
    // Only initialize on mobile devices
    if (!this.isMobile()) {
      return;
    }

    // Setup zoom immediately (DOM should be ready when this is called)
    this.setupZoom();
    this.observeChanges();
  }

  observeChanges() {
    // Only observe changes on mobile
    if (!this.isMobile()) {
      return;
    }

    // Watch for dynamically added images (e.g., variant changes)
    const observer = new MutationObserver((mutations) => {
      // Double check we're still on mobile
      if (!this.isMobile()) {
        return;
      }

      let shouldReinit = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Check if new images were added
            if (node.tagName === 'IMG' || node.querySelector && node.querySelector('img')) {
              shouldReinit = true;
            }
          }
        });
      });
      
      if (shouldReinit && this.isMobile()) {
        // Debounce re-initialization
        clearTimeout(this.reinitTimeout);
        this.reinitTimeout = setTimeout(() => {
          this.setupZoom();
        }, 300);
      }
    });

    // Observe the product media wrapper
    const productMediaWrapper = document.querySelector('.product__media-wrapper, .product-slider');
    if (productMediaWrapper) {
      observer.observe(productMediaWrapper, {
        childList: true,
        subtree: true
      });
    }
  }

  setupZoom() {
    // Only setup zoom on mobile
    if (!this.isMobile()) {
      return;
    }
    // Handle both media gallery and media swiper layouts
    this.setupMediaGalleryZoom();
    this.setupMediaSwiperZoom();
  }

  setupMediaGalleryZoom() {
    // Get all product images in the media gallery
    const productImages = document.querySelectorAll(
      '.product__media-wrapper .product__modal-opener--image img, .product__media-wrapper .product__media img'
    );

    productImages.forEach((img) => {
      // Make sure we're only adding to image elements
      if (img.tagName === 'IMG') {
        this.addZoomEffect(img);
      }
    });
  }

  setupMediaSwiperZoom() {
    // Get all product images in the swiper layout
    const swiperImages = document.querySelectorAll(
      '.product-slider .main-slider img.productImage, .product-slider .main-slider .productImgWrapper img, .product-slider .swiper-slide img'
    );

    swiperImages.forEach((img) => {
      if (img.tagName === 'IMG' && !img.closest('.thumb-small-slider')) {
        // Skip thumbnail images
        this.addZoomEffect(img);
      }
    });
  }

  addZoomEffect(img) {
    // Only add zoom effect on mobile
    if (!this.isMobile()) {
      return;
    }

    // Skip if already has zoom effect
    if (img.classList.contains('zoom-enabled')) {
      return;
    }

    // Skip if image is in a button or link (to avoid double-click handlers)
    if (img.closest('button, a')) {
      // Still add zoom effect but handle click differently
    }

    img.classList.add('zoom-enabled');
    
    // Add click event listener (only on mobile)
    const clickHandler = (e) => {
      // Double check we're on mobile
      if (!this.isMobile()) {
        return;
      }
      
      // Check if there's a modal-opener parent
      const modalOpener = img.closest('.product__modal-opener');
      if (modalOpener) {
        // Check if there's a button that should handle the click
        const modalToggle = modalOpener.querySelector('.product__media-toggle');
        if (modalToggle && !e.target.closest('button')) {
          // Trigger the modal button
          e.preventDefault();
          e.stopPropagation();
          modalToggle.click();
          // Add zoom animation to modal
          setTimeout(() => {
            this.enhanceModalZoom();
          }, 100);
        }
      } else {
        // For swiper layout or images without modal, create zoom overlay
        e.preventDefault();
        e.stopPropagation();
        this.createZoomOverlay(img);
      }
    };
    
    img.addEventListener('click', clickHandler, true); // Use capture phase to handle before other handlers

    // Add cursor pointer to indicate clickability (only on mobile)
    if (this.isMobile()) {
      img.style.cursor = 'zoom-in';
    }
  }

  enhanceModalZoom() {
    // Add zoom animation to modal images when they open
    const modal = document.querySelector('.product-media-modal.active, .product-media-modal[open]');
    if (modal) {
      const modalImg = modal.querySelector('img');
      if (modalImg) {
        modalImg.style.animation = 'zoomIn 0.4s ease forwards';
      }
    }
  }

  openModal(modalOpener) {
    const modalId = modalOpener.getAttribute('data-modal');
    if (modalId) {
      const modal = document.querySelector(modalId);
      if (modal && modal.show) {
        modal.show(modalOpener);
      }
    }
  }

  createZoomOverlay(img) {
    // Create overlay container
    const overlay = document.createElement('div');
    overlay.className = 'product-image-zoom-overlay';
    
    // Create zoomed image
    const zoomedImg = document.createElement('img');
    zoomedImg.src = img.src.replace(/\?.*$/, '') + '?width=2000'; // Get high-res version
    zoomedImg.className = 'product-image-zoom-img';
    zoomedImg.alt = img.alt || '';
    
    overlay.appendChild(zoomedImg);
    
    // Add close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'product-image-zoom-close';
    closeBtn.innerHTML = '×';
    closeBtn.setAttribute('aria-label', 'Close zoom');
    overlay.appendChild(closeBtn);
    
    // Add to body
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    
    // Trigger zoom animation
    requestAnimationFrame(() => {
      overlay.classList.add('active');
    });
    
    // Close on click outside or close button
    const closeZoom = () => {
      overlay.classList.remove('active');
      setTimeout(() => {
        document.body.removeChild(overlay);
        document.body.style.overflow = '';
      }, 300);
    };
    
    closeBtn.addEventListener('click', closeZoom);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closeZoom();
      }
    });
    
    // Close on Escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeZoom();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
  }
}

// Clean up any existing zoom-enabled classes on desktop
const cleanupDesktopZoom = () => {
  if (window.innerWidth > 750) {
    document.querySelectorAll('.zoom-enabled').forEach(img => {
      img.classList.remove('zoom-enabled');
      img.style.cursor = '';
    });
  }
};

// Initialize on page load (only on mobile)
let productImageZoom;

// Function to initialize zoom
const initializeZoom = () => {
  // Cleanup desktop first
  cleanupDesktopZoom();
  
  // Only initialize on mobile
  if (window.innerWidth <= 750 || window.matchMedia('(max-width: 750px)').matches) {
    // Small delay to ensure images are in DOM
    setTimeout(() => {
      productImageZoom = new ProductImageZoom();
    }, 100);
  }
};

// Wait for DOM to be ready before initializing
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeZoom);
} else {
  // DOM is already ready, initialize immediately
  initializeZoom();
}

// Re-initialize when product images are dynamically loaded (e.g., variant changes)
if (typeof document.addEventListener !== 'undefined') {
  document.addEventListener('product:media-change', () => {
    setTimeout(() => {
      // Only re-initialize if mobile
      if (window.innerWidth <= 750) {
        new ProductImageZoom();
      }
    }, 100);
  });
}

// Handle window resize to enable/disable zoom functionality
let resizeTimeout;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    // Remove zoom-enabled class from all images if switching to desktop
    if (window.innerWidth > 750) {
      cleanupDesktopZoom();
    }
  }, 250);
});

